import { computed, ref, watchEffect, type ComputedRef, type Ref } from 'vue';
import { useDragTransform } from './useDragTransform';

export function useColumnDrag(params: {
  tableRef: Ref<HTMLElement | undefined>;
  /* for correct indexing (visible index != real index) */
  columnOrder: Ref<string[]>;
  visibleColumnOrder: ComputedRef<string[]>;
  /* for correct offset-calculation */
  columnWidths: Ref<Map<string, number>>;
  /** Rendered width, including responsive growth; stored widths are only bases. */
  getColumnWidth?: (columnId: string) => number;
  onColumnMoved: (newPositions: Map<string, number>) => void;
}) {
  const draggedColumn = ref<string | null>(null);
  const draggedColumnPreviewTargetId = ref<string | null>(null);

  /**
   * Set true on the first dragenter after dragstart. Gates the source-column
   * bridge drop-zone — only rendered after the user has moved off the source,
   * so Chrome doesn't cancel the drag on a zone overlapping the dragged
   * element at dragstart.
   */
  const hasMoved = ref(false);

  const { setOffset: setColOffset, clearOffsets: clearColOffsets } = useDragTransform(
    params.tableRef,
    'col'
  );

  // calculate indices/targets for drop zones
  const columnDropZoneIds = computed(() => {
    const dragged = draggedColumn.value;
    if (dragged === null) return [] as string[];
    // copy with 2x the dragged column (as 50% left to own center and own center to 50% right is NOPs)
    const result: string[] = [];
    for (const c of params.visibleColumnOrder.value) {
      result.push(c);
      if (c === dragged) result.push(c);
    }
    return result;
  });

  /**
   * Late-emit bridge drop-zone descriptor. Covers the dragged column's own
   * slot, anchored to the columns immediately before and after it. Anchoring
   * to the neighbours (not to the dragged column itself) keeps the zone's
   * position stable: the dragged column carries a CSS transform that follows
   * the preview-target, so anchoring there would make the zone chase its own
   * preview. Dropping into this bridge is a no-op.
   *
   * `null` means: no drag, or the user has not moved yet. The zone must not
   * be rendered at dragstart — Chrome cancels the drag if a drop-zone
   * overlaps the dragged element at the moment the drag begins.
   *
   * `leftAnchorIdx`/`rightAnchorIdx` are visible-column indices; `null` on
   * either side signals the edge fallback (no neighbouring column on that
   * side, so anchor to the container edge instead).
   */
  const columnBridgeZone = computed<{
    leftAnchorIdx: number | null;
    rightAnchorIdx: number | null;
  } | null>(() => {
    if (!hasMoved.value || draggedColumn.value === null) {
      return null;
    }
    const visible = params.visibleColumnOrder.value;
    const srcIdx = visible.indexOf(draggedColumn.value);
    if (srcIdx === -1) {
      return null;
    }
    return {
      leftAnchorIdx: srcIdx > 0 ? srcIdx - 1 : null,
      rightAnchorIdx: srcIdx < visible.length - 1 ? srcIdx + 1 : null,
    };
  });

  function onColDragStart(event: DragEvent, columnId: string) {
    hasMoved.value = false;
    draggedColumn.value = columnId;
    if (params.tableRef.value) {
      params.tableRef.value.style.overflowY = 'hidden';
    }
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  let colDragEnterFrame: number | null = null;
  let pendingColDragEnterTarget: string | null = null;
  // drop-zone enter -> set target
  function onColDragEnter(columnId: string) {
    if (!draggedColumn.value) {
      return;
    }
    pendingColDragEnterTarget = columnId;
    if (colDragEnterFrame !== null) {
      return;
    }
    // rAF-throttling
    colDragEnterFrame = requestAnimationFrame(() => {
      colDragEnterFrame = null;
      const target = pendingColDragEnterTarget;
      pendingColDragEnterTarget = null;
      if (target === null || !draggedColumn.value) {
        return;
      }
      draggedColumnPreviewTargetId.value = columnId;
      hasMoved.value = true;
    });
  }

  // drop-zone drop
  function onColDrop() {
    if (!draggedColumn.value) {
      return;
    }

    const previewTargetId = draggedColumnPreviewTargetId.value;

    if (previewTargetId === null || previewTargetId === draggedColumn.value) {
      return;
    }

    const fromIndex = params.columnOrder.value.indexOf(draggedColumn.value);
    const targetIdx = params.columnOrder.value.indexOf(previewTargetId);
    if (fromIndex === -1 || targetIdx === -1 || fromIndex === targetIdx) {
      return;
    }

    // TODO: change this (onColumnMoved) to async callback with only from/to (Promise<void>)
    // and update columnOrder only on success
    const newOrder = [...params.columnOrder.value];
    newOrder.splice(fromIndex, 1);
    const finalIdx = targetIdx;
    newOrder.splice(finalIdx, 0, draggedColumn.value);
    params.columnOrder.value = newOrder;
    const minIndex = Math.min(fromIndex, targetIdx);
    const maxIndex = Math.max(fromIndex, targetIdx);
    const newPositions = new Map(
      newOrder.map((columnId, idx) => [columnId, idx]).slice(minIndex, maxIndex + 1) as [
        string,
        number,
      ][]
    );
    params.onColumnMoved(newPositions);
  }

  // cleanup
  function onColDragEnd() {
    if (colDragEnterFrame !== null) {
      cancelAnimationFrame(colDragEnterFrame);
      colDragEnterFrame = null;
      pendingColDragEnterTarget = null;
    }
    if (params.tableRef.value) {
      params.tableRef.value.style.overflowY = '';
    }
    hasMoved.value = false;
    draggedColumn.value = null;
    draggedColumnPreviewTargetId.value = null;
  }

  // CSS transform effect
  watchEffect(() => {
    const dragged = draggedColumn.value;
    const targetId = draggedColumnPreviewTargetId.value;
    const visible = params.visibleColumnOrder.value;
    const widths = params.columnWidths.value;

    // re calculate offsets
    clearColOffsets();

    if (!dragged || !targetId || dragged === targetId || !params.tableRef.value) {
      return;
    }
    const dragIdx = visible.indexOf(dragged);
    const targetIdx = visible.indexOf(targetId);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) {
      return;
    }
    const widthOf = (columnId: string) =>
      params.getColumnWidth?.(columnId) ?? widths.get(columnId) ?? 0;
    const draggedWidth = widthOf(dragged);

    if (dragIdx < targetIdx) {
      let sum = 0;
      for (let v = dragIdx + 1; v <= targetIdx; v++) {
        const cId = visible[v];
        setColOffset(cId, `${-draggedWidth}px`);
        sum += widthOf(cId);
      }
      setColOffset(dragged, `${sum}px`);
    } else {
      let sum = 0;
      for (let v = targetIdx; v < dragIdx; v++) {
        const cId = visible[v];
        setColOffset(cId, `${draggedWidth}px`);
        sum += widthOf(cId);
      }
      setColOffset(dragged, `${-sum}px`);
    }
  });

  return {
    draggedColumn,
    draggedColumnPreviewTargetId,
    columnDropZoneIds,
    onColDragStart,
    onColDragEnter,
    onColDrop,
    onColDragEnd,
    hasMoved,
    columnBridgeZone,
  };
}
